import { useQuery } from "@apollo/client";
import { Box, CircularProgress } from "@mui/material";
import { ReactElement } from "react";

import {
  AudienceType,
  GetAudienceSummaryQuery,
  GetAudienceSummaryQueryVariables,
  GetSubscriberSummaryQuery,
  GetSubscriberSummaryQueryVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { GET_AUDIENCE_SUMMARY } from "~frontend/graphql/audience";
import { GET_SUBSCRIBER_SUMMARY } from "~frontend/graphql/shortcode";
import { Audience } from "~frontend/routes/subaccounts/audiences/Subscribers/types";
import { plural } from "~frontend/util/plural";

function OptInAudienceSummary({
  audience,
}: {
  audience: Audience;
}): ReactElement {
  const summaryQuery = useQuery<
    GetSubscriberSummaryQuery,
    GetSubscriberSummaryQueryVariables
  >(GET_SUBSCRIBER_SUMMARY, {
    variables: {
      input: {
        audienceId: audience!.id,
      },
    },
    fetchPolicy: "network-only",
  });

  if (summaryQuery.loading) {
    return (
      <>
        <CircularProgress size={16} />
        Loading subscriber summary...
      </>
    );
  }

  if (summaryQuery.error) {
    return <>Error loading subscriber summary</>;
  }

  return (
    <FlexBox gap={2} alignItems="center">
      <Box sx={{ fontSize: "12px" }}>
        {plural(
          summaryQuery.data!.getSubscriberSummary.activeSubscribers,
          "Contact",
          "Contacts"
        )}
      </Box>
      <Box sx={{ fontSize: "12px" }}>
        {plural(
          summaryQuery.data!.getSubscriberSummary.unsubscribedSubscribers,
          "Opted Out Contact",
          "Opted Out Contacts"
        )}
      </Box>
    </FlexBox>
  );
}

function GeneralAudienceSummary({
  audience,
}: {
  audience: Audience;
}): ReactElement {
  const summaryQuery = useQuery<
    GetAudienceSummaryQuery,
    GetAudienceSummaryQueryVariables
  >(GET_AUDIENCE_SUMMARY, {
    variables: {
      input: {
        audienceId: audience!.id,
      },
    },
    fetchPolicy: "network-only",
  });

  if (summaryQuery.loading) {
    return (
      <>
        <CircularProgress size={16} />
        Loading audience summary...
      </>
    );
  }

  if (summaryQuery.error) {
    return <>Error loading audience summary</>;
  }

  return (
    <FlexBox gap={2} alignItems="center">
      <Box sx={{ fontSize: "12px" }}>
        {plural(
          summaryQuery.data!.getAudienceSummary.activeContacts,
          "Contact",
          "Contacts"
        )}
      </Box>
      <Box sx={{ fontSize: "12px" }}>
        {plural(
          summaryQuery.data!.getAudienceSummary.optedOutContacts,
          "Opted Out Contact",
          "Opted Out Contacts"
        )}
      </Box>
    </FlexBox>
  );
}

export function AudienceSummary({
  audience,
}: {
  audience: Audience;
}): ReactElement {
  return audience.type === AudienceType.Subscribers ? (
    <OptInAudienceSummary audience={audience} />
  ) : (
    <GeneralAudienceSummary audience={audience} />
  );
}
